import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'
import { useNavigate } from 'react-router-dom'

export default function Settings() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [activeTab, setActiveTab] = useState('profile')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [profile, setProfile] = useState({
    full_name: '',
    phone: '',
    credentials: ''
  })
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [exportLoading, setExportLoading] = useState(false)

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (!error && data) {
      setProfile({
        full_name: data.full_name || '',
        phone: data.phone || '',
        credentials: data.credentials || ''
      })
    }
    setLoading(false)
  }

  const handleProfileSave = async () => {
    setError(null)
    setSuccess(null)
    setSaving(true)

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: profile.full_name.trim(),
        phone: profile.phone.trim(),
        credentials: profile.credentials.trim()
      })
      .eq('id', user.id)

    if (error) {
      setError(error.message)
    } else {
      setSuccess('Profile updated successfully.')
    }

    setSaving(false)
  }

  const handlePasswordChange = async () => {
    setError(null)
    setSuccess(null)

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setChangingPassword(true)

    const { error } = await supabase.auth.updateUser({
      password: passwordData.newPassword
    })

    if (error) {
      setError(error.message)
    } else {
      setSuccess('Password updated successfully.')
      setPasswordData({ newPassword: '', confirmPassword: '' })
    }

    setChangingPassword(false)
  }

  const handleExportData = async () => {
    setExportLoading(true)
    setError(null)

    try {
      // Fetch all user data
      const { data: trusts } = await supabase
        .from('trust_members')
        .select('trusts(*)')
        .eq('user_id', user.id)

      const trustIds = trusts?.map(t => t.trusts?.id).filter(Boolean) || []

      const { data: transactions } = await supabase
        .from('transactions')
        .select('*')
        .in('trust_id', trustIds)

      const { data: accounts } = await supabase
        .from('accounts')
        .select('*')
        .in('trust_id', trustIds)

      const { data: beneficiaries } = await supabase
        .from('beneficiaries')
        .select('*')
        .in('trust_id', trustIds)

      const exportData = {
        exported_at: new Date().toISOString(),
        user_email: user.email,
        trusts: trusts?.map(t => t.trusts) || [],
        accounts: accounts || [],
        beneficiaries: beneficiaries || [],
        transactions: transactions || []
      }

      // Download as JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `trustledger_export_${new Date().toISOString().split('T')[0]}.json`
      a.click()
      URL.revokeObjectURL(url)

      setSuccess('Data exported successfully.')
    } catch (err) {
      setError('Export failed: ' + err.message)
    }

    setExportLoading(false)
  }

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This will permanently delete all your trusts, transactions, and documents. This cannot be undone.'
    )
    if (!confirmed) return

    const doubleConfirmed = window.confirm(
      'This is your final warning. All data will be permanently deleted. Are you absolutely sure?'
    )
    if (!doubleConfirmed) return

    // Sign out and show message
    await supabase.auth.signOut()
    navigate('/login')
  }

  const tabs = ['profile', 'password', 'subscription', 'data']

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading settings...</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <h2 className="text-xl font-bold text-gray-900">Settings</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your account and preferences</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab)
                  setError(null)
                  setSuccess(null)
                }}
                className={`pb-3 text-sm font-medium capitalize transition-colors ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'data' ? 'Data & Privacy' : tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <h3 className="font-semibold text-gray-800">Profile Information</h3>

            {/* Email (read only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={user.email}
                disabled
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">
                Email cannot be changed here. Contact support if needed.
              </p>
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={profile.full_name}
                onChange={e => setProfile({ ...profile, full_name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your full name"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="tel"
                value={profile.phone}
                onChange={e => setProfile({ ...profile, phone: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(616) 555-0100"
              />
            </div>

            {/* Credentials */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Professional Credentials <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={profile.credentials}
                onChange={e => setProfile({ ...profile, credentials: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. CPA, JD, CFP"
              />
            </div>

            <button
              onClick={handleProfileSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            <h3 className="font-semibold text-gray-800">Change Password</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Minimum 8 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Re-enter new password"
              />
            </div>

            <button
              onClick={handlePasswordChange}
              disabled={changingPassword}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg transition disabled:opacity-50"
            >
              {changingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Current Plan</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-blue-900">TrustLedger Pro</p>
                    <p className="text-sm text-blue-700 mt-0.5">Annual subscription</p>
                  </div>
                  <span className="bg-blue-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Active
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600 mb-6">
                <p>✓ Unlimited trusts</p>
                <p>✓ Unlimited transactions</p>
                <p>✓ PDF report generation</p>
                <p>✓ Document storage</p>
                <p>✓ Multi-user collaboration</p>
                <p>✓ Audit trail</p>
              </div>
              <div className="pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500 mb-3">
                  Billing and payment management coming soon via Stripe.
                </p>
                <button
                  disabled
                  className="border border-gray-300 text-gray-400 font-medium px-6 py-2.5 rounded-lg cursor-not-allowed text-sm"
                >
                  Manage Billing (Coming Soon)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data & Privacy Tab */}
        {activeTab === 'data' && (
          <div className="space-y-4">

            {/* Export */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-2">Export Your Data</h3>
              <p className="text-sm text-gray-500 mb-4">
                Download all your trust data as a JSON file. This includes all trusts,
                accounts, beneficiaries, and transactions.
              </p>
              <button
                onClick={handleExportData}
                disabled={exportLoading}
                className="bg-gray-800 hover:bg-gray-900 text-white font-medium px-6 py-2.5 rounded-lg transition disabled:opacity-50 text-sm"
              >
                {exportLoading ? 'Exporting...' : '⬇ Export All Data'}
              </button>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-xl border border-red-200 p-6">
              <h3 className="font-semibold text-red-700 mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-500 mb-4">
                Permanently delete your account and all associated data. This action
                cannot be undone. We recommend exporting your data first.
              </p>
              <button
                onClick={handleDeleteAccount}
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg transition text-sm"
              >
                Delete Account
              </button>
            </div>
          </div>
        )}

      </div>
    </Layout>
  )
}